import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type { SkillSummary } from '../../types/skill'
import type {
  Project,
  ProjectCreate,
  ProjectMember,
  ProjectMemberInput,
  Model,
  Tool,
  RegisteredTable,
  RegisteredStorageFolder,
  Prompt,
  Skill,
} from './types'

/** Lifecycle status of a project asset access request. */
export type ProjectAccessRequestStatus = 'requested' | 'approved' | 'open'

// Projects API
export const projectsApi = {
  list: (params?: {
    search?: string
    creator_id?: string
    page?: number
    page_size?: number
    signal?: AbortSignal
    tenantId?: string
  }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Project[]; total: number }>(`/projects${query}`, { signal, tenantId })
  },

  /**
   * List ALL projects (unpaginated). Use for dropdowns/selects that need the full list.
   * writable_only: exclude viewer-only memberships (fork/create targets).
   */
  listAll: (params?: { search?: string; writable_only?: boolean; signal?: AbortSignal; tenantId?: string }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Project[]; total: number }>(`/projects/all${query}`, { signal, tenantId })
  },

  get: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<Project>(`/projects/${id}`, { signal: options?.signal, tenantId: options?.tenantId }),

  create: (data: ProjectCreate) =>
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<ProjectCreate>) =>
    request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string, options?: { force?: boolean; signal?: AbortSignal }) => {
    const q = new URLSearchParams()
    if (options?.force) q.set('force', 'true')
    const query = q.toString()
    const querySuffix = query ? `?${query}` : ''
    return request<void>(`/projects/${id}${querySuffix}`, {
      method: 'DELETE',
      signal: options?.signal,
    })
  },

  bulkDelete: (projectIds: string[], options?: { force?: boolean; signal?: AbortSignal }) => {
    const q = new URLSearchParams()
    if (options?.force) q.set('force', 'true')
    const query = q.toString()
    const querySuffix = query ? `?${query}` : ''
    return request<{ deleted: number; skipped: number; errors: string[] }>(
      `/projects/bulk-delete${querySuffix}`,
      {
        method: 'POST',
        body: JSON.stringify({ project_ids: projectIds }),
        signal: options?.signal,
      }
    )
  },

  updateConfiguration: (
    projectId: string,
    config: {
      models?: { add?: string[]; remove?: string[] }
      tools?: { add?: string[]; remove?: string[] }
      knowledge?: { add?: string[]; remove?: string[] }
      orchestrators?: { add?: string[]; remove?: string[] }
    }
  ) =>
    request<{
      models: { added: number; removed: number; skipped: number; errors: string[] }
      tools: { added: number; removed: number; skipped: number; errors: string[] }
      knowledge: { added: number; removed: number; skipped: number; errors: string[] }
      orchestrators: { added: number; removed: number; skipped: number; errors: string[] }
    }>(`/projects/${projectId}/configuration`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  updateMembers: (id: string, members: ProjectMemberInput[]) =>
    request<void>(`/projects/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ members }),
    }),

  // Member management
  listMembers: (projectId: string) => request<ProjectMember[]>(`/projects/${projectId}/members`),

  addMember: (projectId: string, data: { user_id: string; role: string }) =>
    request<ProjectMember>(`/projects/${projectId}/members/add`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMemberRole: (projectId: string, userId: string, role: string) =>
    request<ProjectMember>(`/projects/${projectId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  removeMember: (projectId: string, userId: string) =>
    request<void>(`/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
    }),

  bulkUpdateMemberRoles: (projectId: string, userIds: string[], role: string) =>
    request<{ updated: number; removed: number; skipped: number; errors: string[] }>(
      `/projects/${projectId}/members/bulk-role`,
      {
        method: 'PUT',
        body: JSON.stringify({ user_ids: userIds, role }),
      }
    ),

  bulkRemoveMembers: (projectId: string, userIds: string[]) =>
    request<{ updated: number; removed: number; skipped: number; errors: string[] }>(
      `/projects/${projectId}/members/bulk-remove`,
      {
        method: 'POST',
        body: JSON.stringify({ user_ids: userIds }),
      }
    ),

  // Resource management
  listModels: (
    projectId: string,
    params?: { search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: Model[]; total: number }>(`/projects/${projectId}/models${query}`)
  },
  listTools: (
    projectId: string,
    params?: { search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: Tool[]; total: number }>(`/projects/${projectId}/tools${query}`)
  },
  listKnowledge: (
    projectId: string,
    params?: {
      search?: string
      page?: number
      page_size?: number
      /** If true, only graph DB tables (Neo4j, Neptune, Cosmos). */
      graph_only?: boolean
      /** Default true on server: exclude graph tables from the Knowledge list. */
      exclude_graph?: boolean
      /** Default true on server: exclude vector DB tables from the Knowledge list. */
      exclude_vector_db?: boolean
      /** If true, only vector DB / vector-classified registered tables. */
      vector_only?: boolean
    }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: RegisteredTable[]; total: number }>(
      `/projects/${projectId}/knowledge${query}`
    )
  },
  listStorage: (
    projectId: string,
    params?: { search?: string; page?: number; page_size?: number; is_favourite?: boolean }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: RegisteredStorageFolder[]; total: number }>(
      `/projects/${projectId}/storage${query}`
    )
  },

  /**
   * List ALL knowledge tables mapped to a project (unpaginated).
   * Use this for dropdowns/selects that need all options.
   */
  listAllKnowledge: (
    projectId: string,
    params?: {
      search?: string
      graph_only?: boolean
      exclude_graph?: boolean
      exclude_vector_db?: boolean
      vector_only?: boolean
      knowledge_type?: 'data' | 'benchmarks'
    }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: RegisteredTable[]; total: number }>(
      `/projects/${projectId}/knowledge/all${query}`
    )
  },
  addModel: (projectId: string, modelId: string) =>
    request<Model>(`/projects/${projectId}/models/${modelId}`, {
      method: 'POST',
    }),

  removeModel: (projectId: string, modelId: string) =>
    request<void>(`/projects/${projectId}/models/${modelId}`, {
      method: 'DELETE',
    }),

  addTool: (projectId: string, toolId: string) =>
    request<Tool>(`/projects/${projectId}/tools/${toolId}/map`, {
      method: 'POST',
    }),

  removeTool: (projectId: string, toolId: string) =>
    request<void>(`/projects/${projectId}/tools/${toolId}/map`, {
      method: 'DELETE',
    }),

  addKnowledge: (projectId: string, knowledgeId: string) =>
    request<RegisteredTable>(`/projects/${projectId}/knowledge/${knowledgeId}/map`, {
      method: 'POST',
    }),

  removeKnowledge: (projectId: string, knowledgeId: string) =>
    request<void>(`/projects/${projectId}/knowledge/${knowledgeId}/map`, {
      method: 'DELETE',
    }),

  // Storage management

  listAllStorage: (projectId: string, params?: { search?: string }) => {
    const query = buildQueryString(params)
    return request<{ items: RegisteredStorageFolder[]; total: number }>(
      `/projects/${projectId}/storage/all${query}`
    )
  },

  addStorage: (projectId: string, storageId: string) =>
    request<RegisteredStorageFolder>(`/projects/${projectId}/storage/${storageId}/map`, {
      method: 'POST',
    }),

  removeStorage: (projectId: string, storageId: string) =>
    request<void>(`/projects/${projectId}/storage/${storageId}/map`, {
      method: 'DELETE',
    }),

  requestStorageAccess: (projectId: string, storageId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/${projectId}/storage/${storageId}/map`, { method: 'POST' }),

  // Orchestrator management
  listProjectOrchestrators: (
    projectId: string,
    params?: { search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{ items: unknown[]; total: number }>(`/projects/${projectId}/orchestrators${query}`)
  },

  addOrchestratorToProject: (projectId: string, orchestratorId: string) =>
    request<unknown>(`/projects/${projectId}/orchestrators/${orchestratorId}/map`, {
      method: 'POST',
    }),

  removeOrchestratorFromProject: (projectId: string, orchestratorId: string) =>
    request<void>(`/projects/${projectId}/orchestrators/${orchestratorId}/map`, {
      method: 'DELETE',
    }),

  // Skills management
  listSkills: (
    projectId: string,
    params?: { search?: string; page?: number; page_size?: number; signal?: AbortSignal }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Skill[]; total: number; page?: number; page_size?: number }>(
      `/projects/${projectId}/skills${query}`,
      { signal }
    )
  },

  /** All project-accessible skills (unpaginated summaries) for name resolution + catalogs */
  listAllSkills: (projectId: string, options?: { model_id?: string; signal?: AbortSignal }) => {
    const { signal, ...queryParams } = options || {}
    const query = buildQueryString(queryParams)
    return request<{ items: SkillSummary[] }>(`/projects/${projectId}/skills/all${query}`, {
      signal,
    })
  },

  addSkill: (projectId: string, skillId: string) =>
    request<Skill>(`/projects/${projectId}/skills/${skillId}/map`, {
      method: 'POST',
    }),

  removeSkill: (projectId: string, skillId: string) =>
    request<void>(`/projects/${projectId}/skills/${skillId}/map`, {
      method: 'DELETE',
    }),

  // Prompts listing (read-only)
  listPrompts: (
    projectId: string,
    params?: {
      search?: string
      category?: string
      page?: number
      page_size?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Prompt[]; total: number }>(`/projects/${projectId}/prompts${query}`, {
      signal,
    })
  },

  // Asset access requests
  requestModelAccess: (projectId: string, modelId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/${projectId}/models/${modelId}/request-access`, { method: 'POST' }),

  requestToolAccess: (projectId: string, toolId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/${projectId}/tools/${toolId}/request-access`, { method: 'POST' }),

  requestKnowledgeAccess: (projectId: string, knowledgeId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/${projectId}/knowledge/${knowledgeId}/request-access`, { method: 'POST' }),

  bulkRequestKnowledgeAccess: (projectId: string, knowledgeIds: string[]) =>
    request<{ added: number; removed: number; skipped: number; errors: string[] }>(
      `/projects/${projectId}/knowledge/bulk-request-access`,
      {
        method: 'POST',
        body: JSON.stringify({ knowledge_ids: knowledgeIds }),
      }
    ),

  requestSkillAccess: (projectId: string, skillId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/${projectId}/skills/${skillId}/request-access`, { method: 'POST' }),

  // Bulk access requests (single API call for all assets of a type)
  listBulkAccessRequests: (
    assetType: 'models' | 'tools' | 'knowledge' | 'storage' | 'skills' | 'orchestrators',
    params?: {
      status?: string
      search?: string
      page?: number
      page_size?: number
      project_id?: string
      requested_by?: string
      // Knowledge-only sub-scope: keep Knowledge / Graph / Vector DB tabs distinct
      graph_only?: boolean
      exclude_graph?: boolean
      vector_only?: boolean
      exclude_vector_db?: boolean
    }
  ) => {
    const query = buildQueryString({ asset_type: assetType, ...params })
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/access-requests/bulk${query}`)
  },

  bulkDecideAccessRequests: (
    items: Array<{
      asset_type: 'models' | 'tools' | 'knowledge' | 'storage' | 'skills' | 'orchestrators'
      asset_id: string
      request_id: string
      action: 'approve' | 'reject'
    }>
  ) =>
    request<{ processed: number; skipped: number; errors: string[] }>(
      '/projects/access-requests/bulk-decide',
      {
        method: 'POST',
        body: JSON.stringify({ items }),
      }
    ),

  // Access management (for asset admins)
  listModelAccessRequests: (
    modelId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/models/${modelId}/access-requests${query}`)
  },

  listToolAccessRequests: (
    toolId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/tools/${toolId}/access-requests${query}`)
  },

  listKnowledgeAccessRequests: (
    knowledgeId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/knowledge/${knowledgeId}/access-requests${query}`)
  },

  listSkillAccessRequests: (
    skillId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/skills/${skillId}/access-requests${query}`)
  },

  listStorageAccessRequests: (
    storageId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/storage/${storageId}/access-requests${query}`)
  },

  listOrchestratorAccessRequests: (
    orchestratorId: string,
    params?: { status?: string; search?: string; page?: number; page_size?: number }
  ) => {
    const query = buildQueryString(params)
    return request<{
      items: Array<{
        id: string
        project_id: string
        project_name?: string
        asset_type: string
        asset_id: string
        asset_name?: string
        status: ProjectAccessRequestStatus
        requested_by?: string
        requested_by_name?: string
        requested_at: number
        updated_at: number
      }>
      total: number
    }>(`/projects/orchestrators/${orchestratorId}/access-requests${query}`)
  },

  approveModelAccess: (modelId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/models/${modelId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  approveToolAccess: (toolId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/tools/${toolId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  approveKnowledgeAccess: (knowledgeId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/knowledge/${knowledgeId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  approveSkillAccess: (skillId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/skills/${skillId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  revokeModelAccess: (modelId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/models/${modelId}/access-requests/${requestId}/revoke`, { method: 'POST' }),

  revokeToolAccess: (toolId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/tools/${toolId}/access-requests/${requestId}/revoke`, { method: 'POST' }),

  revokeKnowledgeAccess: (knowledgeId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/knowledge/${knowledgeId}/access-requests/${requestId}/revoke`, {
      method: 'POST',
    }),

  revokeSkillAccess: (skillId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/skills/${skillId}/access-requests/${requestId}/revoke`, { method: 'POST' }),

  approveStorageAccess: (storageId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/storage/${storageId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  revokeStorageAccess: (storageId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/storage/${storageId}/access-requests/${requestId}/revoke`, { method: 'POST' }),

  approveOrchestratorAccess: (orchestratorId: string, requestId: string, action: 'approve' | 'reject') =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/orchestrators/${orchestratorId}/access-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  revokeOrchestratorAccess: (orchestratorId: string, requestId: string) =>
    request<{
      id: string
      project_id: string
      asset_type: string
      asset_id: string
      status: string
    }>(`/projects/orchestrators/${orchestratorId}/access-requests/${requestId}/revoke`, { method: 'POST' }),

  // Access statistics
  getModelAccessStatistics: (modelId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/models/${modelId}/access-statistics`
    ),

  getToolAccessStatistics: (toolId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/tools/${toolId}/access-statistics`
    ),

  getKnowledgeAccessStatistics: (knowledgeId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/knowledge/${knowledgeId}/access-statistics`
    ),

  getSkillAccessStatistics: (skillId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/skills/${skillId}/access-statistics`
    ),

  getStorageAccessStatistics: (storageId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/storage/${storageId}/access-statistics`
    ),

  getOrchestratorAccessStatistics: (orchestratorId: string) =>
    request<{ projects_with_access: number; open_requests: number }>(
      `/projects/orchestrators/${orchestratorId}/access-statistics`
    ),
}
